import { useCallback, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RecaptchaAction } from '@linktivity/ars-neo';
import {
  Button,
  Form,
  Input,
  Link,
  type FormValues,
  type Rules
} from '@linktivity/link-ui';
import { EMAIL_REGEX, PASSWORD_REGEX } from '@link/share/constants';
import { isOtpError } from '@link/share/services/consts';
import { BaseAuth } from '@link/share/stores';
import { buildLocalizedPath } from '@link/share/utils';
import { getRecaptchaToken, trackGa4Login } from '@link/share/vendors';
import ShareAuth from '@link/share/vendors/firebase';
import styles from './login.module.css';

interface SigninProps {
  initialEmail?: string;
  auth: BaseAuth;
  firebase: ShareAuth;
  onBack?: () => void;
  onAuthSuccess?: () => void | Promise<void>;
  /** See `requireVerified` on Login. */
  requireVerified?: boolean;
}

const Signin = ({
  initialEmail = '',
  auth,
  firebase,
  onBack,
  onAuthSuccess,
  requireVerified
}: SigninProps) => {
  const [formValues, setFormValues] = useState<FormValues>({
    email: initialEmail,
    password: ''
  });
  const [valid, setValid] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const rules: Rules = useMemo(
    () => ({
      email: [
        {
          required: true,
          message: t('share.validation.required', {
            field: t('share.login.email')
          }),
          trigger: ['change', 'blur']
        },
        {
          pattern: EMAIL_REGEX,
          message: t('share.validation.invalid', {
            field: t('share.login.email')
          }),
          trigger: 'blur'
        }
      ],
      password: [
        {
          required: true,
          message: t('share.validation.required', {
            field: t('share.login.password')
          }),
          trigger: ['change', 'blur']
        },
        {
          pattern: PASSWORD_REGEX,
          message: t('share.validation.password'),
          trigger: 'blur'
        }
      ]
    }),
    [t]
  );

  useEffect(() => {
    if (initialEmail) {
      setFormValues(prev => ({ ...prev, email: initialEmail }));
    }
  }, [initialEmail]);

  const submitForm = useCallback(async () => {
    setLoading(true);
    const { email, password } = formValues;
    firebase
      .login(email, password)
      .then(async credential => {
        if (!credential) return;
        // Email verification reads the user from the store: set it here rather
        // than rely on Firebase notifying onAuthStateChanged before resolving.
        auth.setUser(credential.user);
        try {
          const recaptchaToken = await getRecaptchaToken(
            RecaptchaAction.RecaptchaActionLogin
          );
          await auth.updateToken(credential, recaptchaToken);
          if (onAuthSuccess && requireVerified) {
            await onAuthSuccess();
          } else {
            // No verification page follows: this exchange, done without a
            // code, is the sign-in. The caller reacts to loggedIn.
            trackGa4Login('email', 'existing', 'no');
          }
        } catch (error: unknown) {
          const response = (error as { response?: Response })?.response;
          if (onAuthSuccess && response) {
            const data = await response.json();
            if (isOtpError(data?.error_id)) {
              await onAuthSuccess();
              return;
            }
          }
          // The response middleware already showed API errors.
          if (!response) firebase.showErrorMessage(error);
        }
      })
      .catch(() => {
        // login() already showed the error.
      })
      .finally(() => {
        setLoading(false);
        setValid(true);
      });
  }, [auth, firebase, formValues, onAuthSuccess, requireVerified]);

  return (
    <div className={styles.content}>
      <Form
        className={styles.form}
        values={formValues}
        onValuesChange={setFormValues}
        onValidateChange={setValid}
        rules={rules}
        onSubmit={submitForm}
        showRequired={false}
      >
        <Form.Item label={t('share.login.email')} field="email" htmlFor="email">
          <Input id="email" disabled={!!initialEmail} />
        </Form.Item>
        <Form.Item
          label={t('share.login.password')}
          field="password"
          htmlFor="password"
        >
          <Input type="password" id="password" />
        </Form.Item>
        <div className={styles.reset}>
          <Link
            to={buildLocalizedPath('/reset-password')}
            className={styles.resetLink}
          >
            {t('share.login.forgotPassword')}
          </Link>
        </div>
        <Form.Item>
          <Button type="submit" disabled={!valid} loading={loading}>
            {t('share.login.button')}
          </Button>
        </Form.Item>
        {onBack && (
          <Form.Item>
            <Button type="button" variant="outlined" onClick={onBack}>
              {t('share.common.return')}
            </Button>
          </Form.Item>
        )}
      </Form>
    </div>
  );
};

export default Signin;
